// src/pages/ChangePassword.jsx

import React, { useState } from "react";
import { Form, Input, Button, Card, Typography, message, Result } from "antd";
import { EyeTwoTone, EyeInvisibleOutlined } from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import { useParams } from "react-router-dom";
import { LockKeyhole } from "lucide-react";
import { useNavigate } from "react-router-dom";

const { Title } = Typography;

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();
  const { request } = useRequest();
  const { id } = useParams();
  const onFinish = (values) => {
    const { newPassword, confirmPassword } = values;
    if (values.newPassword !== values.confirmPassword) {
      message.error("New password and confirm password do not match!");
      return;
    }

    setLoading(true);

    request({
      url: `/auth/reset-password`,
      method: "POST",
      data: {
        token: id,
        password: newPassword,
      },
      onSuccess: (response) => {
        const msg =
          response?.data?.message ||
          "Password changed successfully!";
        ShowToast(msg, Severty.SUCCESS);
        setLoading(false);
        setIsSuccess(true);
      },
      onError: (error) => {
        const apiMessage =
          error?.response?.data?.message ||
          "Failed to change password.";
        ShowToast(apiMessage, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f0f2f5",
      }}
    >
      {!isSuccess ? (
        <Card style={{ width: 400 }}>
          <Title level={3} style={{ textAlign: "center" }}>
            Set New Password
          </Title>
          <Form layout="vertical" onFinish={onFinish}>
            <Form.Item
              label="New Password"
              name="newPassword"
              rules={[
                { required: true, message: "Please enter your new password!" },
                {
                  pattern:
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_\-+={}[\]|\\:;"'<>,.?/~`]).{8,}$/,
                  message:
                    "Password must include uppercase, lowercase, number and special character",
                },
              ]}
            >
              <Input.Password
                prefix={<LockKeyhole />}
                placeholder="New Password"
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>

            <Form.Item
              label="Confirm New Password"
              name="confirmPassword"
              dependencies={["newPassword"]}
              rules={[
                {
                  required: true,
                  message: "Please confirm your new password!",
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newPassword") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Passwords do not match!")
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockKeyhole />}
                placeholder="Confirm Password"
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" block loading={loading}>
                Reset Password
              </Button>
            </Form.Item>
          </Form>
        </Card>
      ) : (
        <Result
          status="success"
          title="Password Changed Successfully!"
          subTitle="You can now log in with your new password."
          extra={[
            <Button key="close" onClick={() => navigate("/login")}>
              Sign In
            </Button>,
          ]}
        />
      )}
    </div>
  );
};

export default ResetPassword;
